import {AfterViewInit, Component, Inject} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {AsyncPipe, NgIf} from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {UtilityService} from '../../../../service/utility.service';
import {MatSnackBar, MatSnackBarRef, TextOnlySnackBar} from '@angular/material/snack-bar';
import {PeoClientApiModel} from '../../../../model/peo-client-api.model';
import {PeoClientService} from '../../../../service/peo-client.service';
import {PeoService} from '../../../../service/peo.service';
import {CustomerApiModel} from '../../../../model/customer-api.model';
import {PeoApiModel} from '../../../../model/peo-api.model';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {catchError, debounceTime, distinctUntilChanged, map, Observable, of, startWith, switchMap, tap} from 'rxjs';
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from '@angular/material/autocomplete';
import {CustomerService} from '../../../../service/customer.service';

/**
 * @class
 * @description This class is responsible for rendering the PEO Client Dialog component.
 * @export
 * @name PeoClientAdminDialogComponent
 */
@Component({
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    MatTooltip,
    NgIf,
    ReactiveFormsModule,
    MatProgressSpinner,
    MatSuffix,
    FormsModule,
    AsyncPipe,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
  ],
  selector: 'app-dialog-admin-peo-client',
  standalone: true,
  styleUrl: './peo-client.component.scss',
  templateUrl: './peo-client.component.html'
})
export class PeoClientAdminDialogComponent implements AfterViewInit {

  /**
   * @default of([])
   * @description This property contains the customer list.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#customerList
   * @property
   * @protected
   * @type {Observable<CustomerApiModel[]>}
   */
  protected customerList: Observable<CustomerApiModel[]> = of([]);

  /**
   * @description This property contains the filtered data source for the customer list.
   * @memberof AutoCompleteInputFormComponent
   * @name AutoCompleteInputFormComponent#customerListFiltered
   * @property
   * @protected
   * @type {Observable<CustomerApiModel[]>}
   */
  protected customerListFiltered: Observable<CustomerApiModel[]> = of([]);

  /**
   * @description This property contains the form group for the PEO Client.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#form
   * @property
   * @protected
   * @type {FormGroup<{
   *       clientId: FormControl<string>,
   *       created: FormControl<Date>,
   *       customer: FormControl<CustomerApiModel>,
   *       peo: FormControl<PeoApiModel>,
   *       updated: FormControl<Date>,
   *   }>}
   */
  protected readonly form: FormGroup<{
    clientId: FormControl<string>,
    created: FormControl<Date>,
    customer: FormControl<CustomerApiModel | null>,
    peo: FormControl<PeoApiModel | null>,
    updated: FormControl<Date>
  }>;

  /**
   * @default true
   * @description This property contains the form disabled status.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#formDisabled
   * @property
   * @protected
   * @type {boolean}
   */
  protected formDisabled: boolean = true;

  /**
   * @default false
   * @description This property contains the new status of the PEO Client.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#isNew
   * @property
   * @protected
   * @readonly
   * @type {boolean}
   */
  protected readonly isNew: boolean;

  /**
   * @default false
   * @description This property contains the loading status of the Customer list
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#loadingCustomerList
   * @property
   * @protected
   * @type {boolean}
   */
  protected loadingCustomerList: boolean = false;

  /**
   * @default false
   * @description This property contains the loading status of the PEO Clients.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#loadingPeoList
   * @property
   * @protected
   * @type {boolean}
   */
  protected loadingPeoList: boolean = false;

  /**
   * @description This property contains the PEO ID to force the form to.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#peoId
   * @property
   * @protected
   * @readonly
   * @type {string}
   */
  protected readonly peoId?: string;

  /**
   * @default []
   * @description This property contains the PEO list.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#peoList
   * @property
   * @protected
   * @type {PeoApiModel[]}
   */
  protected peoList: PeoApiModel[] = [];

  /**
   * @description This property contains the filtered data source for the PEO list.
   * @memberof AutoCompleteInputFormComponent
   * @name AutoCompleteInputFormComponent#peoListFiltered
   * @property
   * @protected
   * @type {Observable<PeoApiModel[]>}
   */
  protected peoListFiltered: Observable<PeoApiModel[]> = of([]);

  /**
   * @constructor
   * @description This method instantiates a new instance of our PEO Client Dialog component.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#constructor()
   * @param {CustomerService} customerService The injected instance of our customer service provider.
   * @param {MatDialogRef<PeoClientAdminDialogComponent>} dialogReference The injected instance of our dialog reference.
   * @param {FormBuilder} formBuilder The injected instance of our form builder.
   * @param {PeoService} peoService The injected instance of our PEO service provider.
   * @param {PeoClientService} service The injected instance of our PEO Client service provider.
   * @param {PeoService} service The injected instance of our PEO service provider.
   * @param {MatSnackBar} snackbar The injected instance of our snack bar service.
   * @param {{ isNew?: boolean, peoClient?: PeoClientApiModel, peoId?: string }} data The data for the dialog.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the customer service provider.
     * @memberof PeoClientAdminDialogComponent
     * @name PeoClientAdminDialogComponent#customerService
     * @private
     * @property
     * @readonly
     * @type {CustomerService}
     */
    private readonly customerService: CustomerService,
    /**
     * @description This property contains the dialog reference.
     * @memberof PeoClientAdminDialogComponent
     * @name PeoClientAdminDialogComponent#dialogReference
     * @param {MatDialogRef<PeoClientAdminDialogComponent>} dialogReference The injected instance of our dialog reference.
     * @private
     * @property
     * @readonly
     * @type {MatDialogRef<PeoClientAdminDialogComponent>}
     */
    private readonly dialogReference: MatDialogRef<PeoClientAdminDialogComponent>,
    /**
     * @description This property contains the form builder.
     * @memberof PeoClientAdminDialogComponent
     * @name PeoClientAdminDialogComponent#formBuilder
     * @property
     * @protected
     * @readonly
     * @type {FormBuilder}
     */
    private readonly formBuilder: FormBuilder,
    /**
     * @description This property contains the PEO service provider.
     * @memberof PeoClientAdminDialogComponent
     * @name PeoClientAdminDialogComponent#peoService
     * @private
     * @property
     * @readonly
     * @type {PeoService}
     */
    private readonly peoService: PeoService,
    /**
     * @description This property contains the PEO Client service provider.
     * @memberof PeoClientAdminDialogComponent
     * @name PeoClientAdminDialogComponent#service
     * @private
     * @property
     * @readonly
     * @type {PeoClientService}
     */
    private readonly service: PeoClientService,
    /**
     * @description This property contains the snack bar service.
     * @memberof PeoClientAdminDialogComponent
     * @name PeoClientAdminDialogComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar,
    /**
     * @description The data for the dialog.
     * @memberof PeoClientAdminDialogComponent
     * @name PeoClientAdminDialogComponent#data
     * @property
     * @public
     * @type {{
     *    isNew?: boolean,
     *    client?: PeoClientApiModel,
     *    peoId?: string
     *  }}
     */
    @Inject(MAT_DIALOG_DATA)
    public data: { customers: CustomerApiModel[], isNew?: boolean, client?: PeoClientApiModel, peoId?: string }
  ) {

    // Set the new flag based on the data.
    this.isNew = this.data.isNew ?? (!this.data.client?.peoId || !this.data.client?.clientId) ?? false;

    // Set the PEO ID from the data.
    this.peoId = this.data.peoId;

    // Reset the PEO ID into the client model.
    if (!this.data.client || !(this.data.client?.peoId) && !!this.peoId)
      this.data.client = {peoId: this.peoId as string} as PeoClientApiModel;

    // Build our form and set it into the instance.
    this.form = this.formBuilder.group({

      // Define our client ID form control.
      clientId: new FormControl<string>(UtilityService.EmptyString, {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our created form control.
      created: new FormControl<Date>(UtilityService.DateTimeUtc(), {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our customer form control.
      customer: new FormControl<CustomerApiModel | null>(null, {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our PEO form control.
      peo: new FormControl<PeoApiModel | null>(null, {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our updated form control.
      updated: new FormControl<Date>(UtilityService.DateTimeUtc(), {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      })
    });
  }

  /**
   * @description This method converts the form to its model variant.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#formToModel()
   * @private
   * @returns {PeoClientApiModel}
   */
  private formToModel(): PeoClientApiModel {

    // Define our model instance from the form and return it.
    return {
      clientId: this.form.controls.clientId.value.trim(),
      created: this.form.controls.created.value,
      customerId: this.form.controls.customer.value!.id,
      peoId: this.form.controls.peo.value!.peoId,
      updated: this.form.controls.updated.value
    } as PeoClientApiModel;
  }

  /**
   * @description This method closes the open dialog.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#cancel()
   * @protected
   * @returns {void}
   */
  protected cancel(): void {

    // Close the open dialog.
    this.dialogReference.close();
  }

  /**
   * @description This method displays the auto-completed value for a customer.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#displayCustomerValue()
   * @param {CustomerApiModel} customer The customer to display.
   * @protected
   */
  protected displayCustomerValue(customer: CustomerApiModel): string {

    // Return the display value of the customer.
    return !!customer && !!customer?.id && !!customer?.name
      ? `${customer.id.toString()} - ${customer.name}` : UtilityService.EmptyString;
  }

  /**
   * @description This method displays the auto-completed value for a PEO.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#displayPeoValue()
   * @param {PeoApiModel} peo The PEO to display.
   * @protected
   */
  protected displayPeoValue(peo: PeoApiModel): string {

    // Return the display value of the PEO.
    return !!peo && !!peo?.peoId && !!peo?.name
      ? `${peo.peoId} - ${peo.name}` : UtilityService.EmptyString;
  }

  /**
   * @description This method filters the customers based on the search term.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#filterCustomerList()
   * @param {string | null} term The search term to filter the customer list by.
   * @protected
   * @returns {Observable<>CustomerApiModel[]>} The customer list items to show.
   */
  protected filterCustomerList(term: string | null): void {

    // Check for a null or empty term to return an empty array.
    if (!term?.trim() || term.trim().length < 3) return;

    // Reset the customer loading flag.
    this.loadingCustomerList = true;

    // We're done, return the search.
    this.customerService.search(term).pipe(debounceTime(300), distinctUntilChanged(),
      catchError((e: Error): Observable<CustomerApiModel[]> => {

        // Send the snackbar notification to the user.
        this.snackbar.open(`Searching for Customer with Term ${term} failed with: ${e.message}`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        });

        // We're done, return an empty array.
        return of([]);
      }), tap((): boolean => this.loadingCustomerList = false))
      .subscribe((c: CustomerApiModel[]): Observable<CustomerApiModel[]> => this.customerList = of(c));
  }

  /**
   * @description This method filters the PEOs based on the search term.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#filterPeoList()
   * @param {string | PeoApiModel | null} term The search term to filter the PEO list by.
   * @protected
   * @returns {PeoApiModel[]} The PEO list items to show.
   */
  protected filterPeoList(term: string | PeoApiModel | null): PeoApiModel[] {

    // If the term or peo is null or empty, return true so we can show the item.
    if (!term) return this?.peoList;

    // Check for an instance model, then return the comparison.
    if (term instanceof PeoApiModel) return this.peoList.filter((p: PeoApiModel): boolean =>
      (p.name?.trim().toLowerCase() === term.name?.trim().toLowerCase()) && (p.peoId === term?.peoId));

    // Localize and sanitize the search term.
    const searchTerm: string = term.trim().toLowerCase();

    // We're done, return the comparison.
    return this.peoList.filter((p: PeoApiModel): boolean =>
      (p.name?.trim()?.toLowerCase() === searchTerm) || (p.peoId?.toString() === searchTerm) ||
      (p.name?.trim()?.toLowerCase()?.indexOf(searchTerm) > -1) || (p.peoId?.toString()?.indexOf(searchTerm) > -1));
  }

  /**
   * @description This method resets the PEO form to the original values.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#reset()
   * @param {CustomerApiModel} [customer] The customer to reset the form to.
   * @protected
   * @returns {void}
   */
  protected reset(customer?: CustomerApiModel): void {

    // Disable the form if it is not already disabled.
    if (!this.formDisabled) {

      // Disable the form.
      this.formDisabled = true;
    }

    // Set the administrator information into the form.
    this.form.patchValue({

      // Patch the client ID into the form.
      clientId: this.data.client?.clientId,

      // Patch the created date into the form.
      created: this.data.client?.created ?? UtilityService.DateTimeUtc(),

      // Patch the customer into the form.
      customer: customer,

      // Patch the PEO into the form.
      peo: this.peoList.filter((p: PeoApiModel): boolean =>
        p.peoId === this.data.client?.peoId || p.peoId === this.peoId)?.at(0),

      // Patch the updated date into the form.
      updated: this.data.client?.updated ?? UtilityService.DateTimeUtc(),
    });

    // Set up the filtering for the PEO list.
    this.peoListFiltered = this.form.controls.peo.valueChanges.pipe(startWith(UtilityService.EmptyString),
      map((t: string | PeoApiModel | null): PeoApiModel[] => this.filterPeoList(t)));

    // Re-enable the form if it was disabled.
    if (this.formDisabled) this.formDisabled = false;
  }

  /**
   * @description This method saves the PEO model.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#save()
   * @protected
   * @returns {void}
   */
  protected save(): void {

    // Disable the form while we're saving.
    this.formDisabled = true;

    // Localize the model.
    const model: PeoClientApiModel = this.formToModel();

    // Save the PEO model.
    this.service.save(model).subscribe({

      // Handle any errors that occur during the save operation.
      error: (e: Error) => {

        // Show an error message.
        this.snackbar.open(`Saving the PEO Client-Customer Mapping for PEO ID ${model.peoId} and Client ID ${model.clientId} failed with:  ${e.message}`,
          'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom'
          });

        // Re-enable the form.
        this.formDisabled = false;
      },

      // Handle the completion of the save operation.
      next: (p: PeoClientApiModel): void => {

        // Show a success message.
        this.snackbar.open(`PEO Client-Customer Mapping for ${p.peoId} saved successfully.`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom'
        });

        // We're done, close the dialog.
        this.dialogReference.close(p);
      }
    })
  }

  /**
   * @description This method sets up the form and loads the customers and PEOs.
   * @memberof PeoClientAdminDialogComponent
   * @method
   * @name PeoClientAdminDialogComponent#setup()
   * @protected
   * @returns {void}
   */
  protected setup(): void {

    // Disable the form.
    this.formDisabled = true;

    // Reset the loading flag for the PEOs.
    this.loadingPeoList = true;

    // Load the PEOs from the service.
    this.peoService.list().subscribe({

      // Handle the completion of the PEO list load.
      complete: (): boolean => this.loadingPeoList = false,

      // Handle any errors that occur during the customer list load.
      error: (e: Error): MatSnackBarRef<TextOnlySnackBar> => this.snackbar.open(`Loading PEO list failed with: ${e.message}`,
        'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        }),

      // Handle the next event for the customer list load.
      next: (p: PeoApiModel[]): void => {

        // Set the PEO list into the instance.
        this.peoList = p;

        // Check for a provided client in the data input.
        if (!this.data.client?.customerId) return this.reset();

        // Reset the loading flag for the customers.
        this.loadingCustomerList = true;

        // Load the customer from the API.
        this.customerService.load(this.data.client.customerId).subscribe({

          // Handle the completion of the customer load.
          complete: (): boolean => this.loadingCustomerList = false,

          // Handle any errors that occur during the customer load.
          error: (e: Error): void => {

            // Reset the loading state flag for the customers into the instance.
            this.loadingCustomerList = false;

            // Send the snackbar notification to the user.
            this.snackbar.open(`Loading customer ${this.data.client?.customerId} failed with: ${e.message}`,
              'Dismiss', {
                duration: 5000,
                horizontalPosition: 'end',
                panelClass: ['snackbar-error'],
                verticalPosition: 'bottom'
              })
          },

          // Handle the next event for the customer load.
          next: (c: CustomerApiModel | null): void => this.reset(c ?? undefined)
        });
      }
    });
  }

  /**
   * @abstract
   * @description This method is called after Angular has fully initialized the component's view.
   * @memberof PeoClientAdminDialogComponent
   * @name PeoClientAdminDialogComponent#ngAfterViewInit()
   * @method
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Set up the form.
    this.setup();
  }
}
